import { create } from "zustand";
import {
  ThemeSlice,
  RegisterSlice,
  SectionSlice,
  MessageSlice,
  AuthSlice,
  OptionSlice,
  OrganizationSlice,
} from "../interfaces";
import { createAuthSlice } from "./Slices/authSlice";
import { persist } from "zustand/middleware";
import { createMessageSlice } from "./Slices/messageSlice";
import { createSectionSlice } from "./Slices/sectionSlice";
import { createRegisterSlice } from "./Slices/registerSlice";
import { createThemeSlice } from "./Slices/themeSlice";
import { darkTheme } from "../styles/themes/dark";
import { lightTheme } from "../styles/themes/light";
import { createOptionSlice } from "./Slices/optionSlice";
import { createOrganizationSlice } from "./Slices/organizationSlice";
import painelService, { type PainelDaRede } from "../services/painel.service";

export const useCombinedStore = create<
  RegisterSlice &
    SectionSlice &
    AuthSlice &
    MessageSlice &
    ThemeSlice &
    OrganizationSlice &
    OptionSlice
>()(
  persist(
    (set, get, api) => ({
      ...createAuthSlice(set, get, api),
      ...createMessageSlice(set, get, api),
      ...createSectionSlice(set, get, api),
      ...createRegisterSlice(set, get, api),
      ...createThemeSlice(set, get, api),
      ...createOptionSlice(set, get, api),
      ...createOrganizationSlice(set, get, api),
    }),
    {
      name: "store",
      /*
       * O TEMA É REIDRATADO DO CÓDIGO, não do localStorage.
       *
       * `themeZustand` guarda o OBJETO inteiro do tema (com todos os hex). Uma
       * vez salvo, ele congela: quem já tinha usado o Central continuava com a
       * paleta antiga para sempre, mesmo depois de o código mudar. Foi o que
       * aconteceu na virada para os tokens do sistema de design — a tela
       * continuou cinza-oliva porque o objeto velho estava no navegador.
       *
       * `merge` roda na reidratação: o que vem do disco entra, e o tema é
       * trocado pelo objeto CANÔNICO do mesmo título. A ESCOLHA da pessoa
       * (claro ou escuro) é preservada; os valores vêm sempre do código.
       */
      merge: (persistido, atual) => {
        const salvo = (persistido ?? {}) as any;
        const titulo = salvo?.themeZustand?.title;
        return {
          ...atual,
          ...salvo,
          themeZustand:
            titulo === "dark"
              ? darkTheme
              : titulo === "light"
              ? lightTheme
              : atual.themeZustand,
        };
      },
      // `messageZustand` SAIU daqui, de propósito.
      //
      // Mensagem é um recado de agora, não estado de sessão. Persistida, ela
      // sobrevivia ao logout: quem visse "Erro ao salvar informações" na
      // Configuração encontrava o mesmo erro pintado na tela de /login na
      // sessão seguinte, sem ter feito nada. Enquanto o MessageBox não estava
      // montado na área privada, isso passava despercebido; com ele montado,
      // seria a primeira coisa a aparecer errada.
      partialize: (state) => {
        const {
          authUserZustand,
          userInfoZustand,
          selectedSectionZustand,
          registerDataZustand,
          themeZustand,
          organizationZustand,
        } = state as any;
        return {
          authUserZustand,
          userInfoZustand,
          selectedSectionZustand,
          registerDataZustand,
          themeZustand,
          organizationZustand,
        };
      },
    }
  )
);
/**
 * AS BARRAS NÃO NASCEM ABERTAS EM TELA ESTREITA.
 *
 * As duas começavam `true` em qualquer largura. Isso é o certo em 1440, onde
 * elas dividem a linha com o conteúdo; abaixo disso elas viram GAVETA (ver
 * MainContent em styles/layouts/layouts.ts) e abrir sozinhas significa o
 * usuário chegar na tela com o trabalho coberto e um fundo escuro por cima.
 *
 * Os limites são os MESMOS de `styles/global/breakpoints.ts` — a direita sai do
 * fluxo abaixo de 1440, a esquerda abaixo de 1280. Se um dia mudarem lá, mudam
 * aqui: é o par que faz a barra abrir só onde ela cabe.
 */
function cabeAberta(limite: number): boolean {
  if (typeof window === "undefined") return true;
  return window.innerWidth >= limite;
}

interface LeftSidebarState {
  isLeftSidebarVisible: boolean;
  toggleLeftSidebar: () => void;
}

export const useLeftSidebarStore = create<LeftSidebarState>((set) => ({
  isLeftSidebarVisible: cabeAberta(1280),
  toggleLeftSidebar: () =>
    set((state) => ({ isLeftSidebarVisible: !state.isLeftSidebarVisible })),
}));

interface RightSidebarState {
  isRightSidebarVisible: boolean;
  toggleRightSidebar: () => void;
}

export const useRightSidebarStore = create<RightSidebarState>((set) => ({
  isRightSidebarVisible: cabeAberta(1440),
  toggleRightSidebar: () =>
    set((state) => ({ isRightSidebarVisible: !state.isRightSidebarVisible })),
}));

/**
 * A REDE, EM UM LUGAR SÓ — para a Home e a barra da direita não discordarem.
 *
 * Antes cada uma tinha a SUA cópia da rede escrita à mão (Welcome.tsx e
 * RightBar.tsx, com nomes e números diferentes entre si), e escolher uma loja
 * num lado não mudava nada no outro. Agora quem busca `/painel/rede` é este
 * store: uma requisição por sessão, e a unidade selecionada é a mesma nos dois.
 *
 * Não entra no `persist`: é dado do servidor, e guardar número de venda no
 * localStorage faria a tela abrir mostrando o resultado de ontem como se fosse
 * o de agora.
 */
interface RedeState {
  painel: PainelDaRede | null;
  carregando: boolean;
  erro: string | null;
  /** subsidiaryUuid da unidade em foco, compartilhado entre as telas. */
  unidadeSelecionada: string | null;
  selecionarUnidade: (uuid: string) => void;
  carregarRede: (filtro?: { de?: string; ate?: string }) => Promise<void>;
}

export const useRedeStore = create<RedeState>((set, get) => ({
  painel: null,
  carregando: false,
  erro: null,
  unidadeSelecionada: null,
  selecionarUnidade: (uuid) => set({ unidadeSelecionada: uuid }),
  carregarRede: async (filtro = {}) => {
    if (get().carregando) return;
    set({ carregando: true });
    try {
      const painel = await painelService.rede(filtro);
      const unidades = painel.companies.flatMap((c) => c.unidades);
      const atual = get().unidadeSelecionada;
      const aindaExiste = unidades.some((u) => u.uuid === atual);
      set({
        painel,
        erro: null,
        carregando: false,
        // Foco inicial na unidade que OPERA: é a única que tem número para ver.
        unidadeSelecionada: aindaExiste
          ? atual
          : (unidades.find((u) => u.operaNoOps) ?? unidades[0])?.uuid ?? null,
      });
    } catch (falha: any) {
      /*
       * SESSÃO VENCIDA NÃO É "FALHA AO CARREGAR" — e dizer a mesma frase para
       * as duas coisas custou uma tarde.
       *
       * Em 03/09/2026 o dono viu "não foi possível carregar a rede" e foi
       * procurar defeito no mapa e no painel. A rede estava de pé; o que tinha
       * vencido era o token dele, de 8 horas. A frase genérica não deu a ele a
       * única informação que resolvia: entrar de novo.
       *
       * O 401 é tratado à parte porque agora ele CHEGA aqui — antes o
       * backend respondia 500 sem cabeçalho de CORS, o navegador bloqueava, e
       * `falha.response` vinha `undefined`. Com o CORS e o status arrumados no
       * servidor, dá para distinguir os três casos que a pessoa vive de forma
       * diferente: sessão vencida, servidor que respondeu erro, e navegador
       * que não conseguiu falar com o servidor.
       */
      const status = falha?.response?.status;
      const semResposta = !falha?.response;
      set({
        carregando: false,
        erro:
          status === 401
            ? "A sua sessão expirou. Entre de novo para ver os números da rede."
            : semResposta
              ? "Não consegui falar com o servidor. Verifique a conexão — o " +
                "painel prefere não mostrar nada a mostrar número inventado."
              : (falha?.response?.data?.message ??
                "Não foi possível carregar a rede. O painel prefere não " +
                  "mostrar nada a mostrar número inventado."),
      });
    }
  },
}));

/**
 * DENSIDADE E ZOOM DAS TABELAS — portado do FlyOps.
 *
 * O padrão vem de `fly-ops-app/SPEC_TABLE_DENSITY.md`, que existe por uma
 * queixa concreta de cliente: "não cabem nem 10 fichas; no Flisys aparecem 40".
 * Lá a Fase 1 (só a lista de voos) subiu de 9 para 14 fichas por tela, +56%, e
 * a Fase 2 levou o MESMO controle para as 24 telas de uma vez, mexendo no
 * componente de tabela COMPARTILHADO em vez de tela por tela.
 *
 * Aqui vale igual: a `DynamicTable` é a tabela de todas as telas de cadastro e
 * de relatório do Central, então um controle só governa o sistema inteiro. E
 * responde à queixa do Guilherme de 28/08: "as coisas estão grandes demais, a
 * tela tem que ficar 100% aproveitada".
 *
 * DUAS COISAS SEPARADAS, de propósito (também do FlyOps):
 *  · densidade  = espaço em volta do texto (padding da célula, altura da linha)
 *  · escala     = tamanho do TEXTO, em três passos (0,9 / 1 / 1,1)
 * Quem tem monitor grande quer mais linha COM a mesma letra; quem tem vista
 * cansada quer letra maior sem perder a densidade. Um controle só não atende os
 * dois.
 *
 * PERSISTIDO: é preferência de pessoa, não estado de sessão. Fica no
 * localStorage e sobrevive ao logout, como no FlyOps.
 */
export type DensidadeDaTabela = "confortavel" | "compacta";

interface TabelaState {
  densidade: DensidadeDaTabela;
  escalaDaFonte: number;
  alternarDensidade: () => void;
  ciclarEscala: () => void;
}

export const useTabelaStore = create<TabelaState>()(
  persist(
    (set) => ({
      densidade: "confortavel",
      escalaDaFonte: 1,
      alternarDensidade: () =>
        set((s) => ({
          densidade: s.densidade === "compacta" ? "confortavel" : "compacta",
        })),
      // 1 -> 1,1 -> 0,9 -> 1. O arredondamento evita 1.0999999999 na tela.
      ciclarEscala: () =>
        set((s) => {
          const proxima = s.escalaDaFonte >= 1.1 ? 0.9 : s.escalaDaFonte + 0.1;
          return { escalaDaFonte: Math.round(proxima * 10) / 10 };
        }),
    }),
    { name: "alfredo-tabela-prefs" },
  ),
);
